import { useEffect } from 'react';
import './DoubleEdgeAnimation.css';

/**
 * PROFESSIONAL DOUBLE-EDGE ANIMATION
 * Normal-type physical move with reckless full-body charge
 * 
 * Key Features:
 * - Normal-type physical attack (120 base power, 100% accuracy)
 * - User takes 1/3 recoil damage
 * - Devastating full-body tackle with energy aura and recoil effect
 * - Professional gaming quality with intense contact impact
 */
export default function DoubleEdgeAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const dx = opponentX - playerX;
    const dy = opponentY - playerY;
    const distance = Math.hypot(dx, dy);

    const elements = [];

    // PHASE 1: Reckless Energy Buildup (0-400ms)
    playerEl.classList.add('double-edge-user-charge');

    const chargeAura = document.createElement('div');
    chargeAura.className = 'double-edge-charge-aura';
    chargeAura.style.left = `${playerX - 70}px`;
    chargeAura.style.top = `${playerY - 70}px`;
    container.appendChild(chargeAura);
    elements.push(chargeAura);

    // Energy streaks gathering around player
    for (let i = 0; i < 10; i++) {
      const streak = document.createElement('div');
      streak.className = 'double-edge-energy-streak';
      const angle = (i / 10) * 360;
      const radius = 80;
      streak.style.left = `${playerX + Math.cos(angle * Math.PI / 180) * radius - 3}px`;
      streak.style.top = `${playerY + Math.sin(angle * Math.PI / 180) * radius - 15}px`;
      streak.style.transform = `rotate(${angle + 180}deg)`;
      streak.style.animationDelay = `${i * 35}ms`;
      container.appendChild(streak);
      elements.push(streak);
    }

    // PHASE 2: Reckless Charge (400-800ms)
    const phase2Timer = setTimeout(() => {
      playerEl.classList.add('double-edge-user-dash');
      playerEl.style.setProperty('--dash-x', `${dx * 0.85}px`);
      playerEl.style.setProperty('--dash-y', `${dy * 0.85}px`);

      // Speed trail during charge
      const trail = document.createElement('div');
      trail.className = 'double-edge-speed-trail';
      trail.style.left = `${playerX - 10}px`;
      trail.style.top = `${playerY - 5}px`;
      trail.style.width = `${distance * 0.85}px`;
      trail.style.transform = `rotate(${Math.atan2(dy, dx) * 180 / Math.PI}deg)`;
      trail.style.transformOrigin = 'left center';
      container.appendChild(trail);
      elements.push(trail);

      // Charge energy particles
      for (let i = 0; i < 12; i++) {
        const pTimer = setTimeout(() => {
          const particle = document.createElement('div');
          particle.className = 'double-edge-charge-particle';
          const progress = i / 12;
          const px = playerX + dx * progress * 0.85 + (Math.random() - 0.5) * 20;
          const py = playerY + dy * progress * 0.85 + (Math.random() - 0.5) * 20;
          particle.style.left = `${px - 4}px`;
          particle.style.top = `${py - 4}px`;
          container.appendChild(particle);
          elements.push(particle);
        }, i * 25);
        elements.push({ timer: pTimer, type: 'timer' });
      }
    }, 400);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Devastating Impact (800-1300ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('double-edge-opponent-hit');
      playerEl.classList.remove('double-edge-user-dash');

      // Massive impact flash
      const impactFlash = document.createElement('div');
      impactFlash.className = 'double-edge-impact-flash';
      impactFlash.style.left = `${opponentX - 100}px`;
      impactFlash.style.top = `${opponentY - 100}px`;
      container.appendChild(impactFlash);
      elements.push(impactFlash);

      // Impact shockwave
      for (let i = 0; i < 3; i++) {
        const swTimer = setTimeout(() => {
          const shockwave = document.createElement('div');
          shockwave.className = 'double-edge-shockwave';
          shockwave.style.left = `${opponentX - 70}px`;
          shockwave.style.top = `${opponentY - 70}px`;
          container.appendChild(shockwave);
          elements.push(shockwave);
        }, i * 100);
        elements.push({ timer: swTimer, type: 'timer' });
      }

      // Impact sparks
      for (let i = 0; i < 18; i++) {
        const spark = document.createElement('div');
        spark.className = 'double-edge-impact-spark';
        const sparkAngle = (i / 18) * 360;
        const sparkDist = 60 + Math.random() * 50;
        spark.style.left = `${opponentX - 3}px`;
        spark.style.top = `${opponentY - 3}px`;
        spark.style.setProperty('--scatter-x', `${Math.cos(sparkAngle * Math.PI / 180) * sparkDist}px`);
        spark.style.setProperty('--scatter-y', `${Math.sin(sparkAngle * Math.PI / 180) * sparkDist}px`);
        spark.style.animationDelay = `${i * 15}ms`;
        container.appendChild(spark);
        elements.push(spark);
      }
    }, 800);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Recoil Effect (1300-1800ms)
    const phase4Timer = setTimeout(() => {
      playerEl.classList.remove('double-edge-user-charge');
      playerEl.classList.add('double-edge-user-recoil');

      // Recoil damage flash on player
      const recoilFlash = document.createElement('div');
      recoilFlash.className = 'double-edge-recoil-flash';
      recoilFlash.style.left = `${playerX - 50}px`;
      recoilFlash.style.top = `${playerY - 50}px`;
      container.appendChild(recoilFlash);
      elements.push(recoilFlash);

      // Recoil sparks around player
      for (let i = 0; i < 6; i++) {
        const recoilSpark = document.createElement('div');
        recoilSpark.className = 'double-edge-recoil-spark';
        const angle = (i / 6) * 360;
        const dist = 30 + Math.random() * 20;
        recoilSpark.style.left = `${playerX - 3}px`;
        recoilSpark.style.top = `${playerY - 3}px`;
        recoilSpark.style.setProperty('--scatter-x', `${Math.cos(angle * Math.PI / 180) * dist}px`);
        recoilSpark.style.setProperty('--scatter-y', `${Math.sin(angle * Math.PI / 180) * dist}px`);
        recoilSpark.style.animationDelay = `${i * 30}ms`;
        container.appendChild(recoilSpark);
        elements.push(recoilSpark);
      }
    }, 1300);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (1800ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('double-edge-user-charge', 'double-edge-user-dash', 'double-edge-user-recoil');
      opponentEl.classList.remove('double-edge-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 1800);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('double-edge-user-charge', 'double-edge-user-dash', 'double-edge-user-recoil');
      opponentEl.classList.remove('double-edge-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

DoubleEdgeAnimation.moveType = 'normal';
